// Pile setting-out schedule — station/offset of every pile against its bridge's own design axis
// (docs/digitwin/Bridges Roadmap.md, Phase 8).
//
// Every OBM bridge is designed on its own "partial clone" of the main axis, and the pile layout follows
// that clone, not main8: the two differ by up to ~19 mm laterally (Bridge_05). The clone is stored with
// start station 0, so its real chainage is recovered from OBM's own support lines: each support line
// (A01, P01 ...) records its station, a point and its direction. Where it crosses the clone, the
// chainage at the clone's start is `station - distance along the clone`, and every support line has to
// agree on it, which picks the right axis among same-named clones and checks the result.
import { tessellateAlignment, projectToAlignment, getExactTangentAtStation } from "../viewer/viewerAlignment.js";

const AXIS_AGREEMENT = 0.001; // m — support lines must agree on the axis chainage within 1 mm

// Where the support line (p0 + t·d) crosses the alignment, refined against the exact geometry
function crossing(line, tess) {
  const [px, py] = line.p0, [dx, dy] = line.d;
  let best = null;
  const pts = tess.points;
  for (let i = 0; i < pts.length - 1; i++) {
    const [ax, ay] = pts[i], [bx, by] = pts[i + 1];
    const ex = bx - ax, ey = by - ay;
    const den = dx * ey - dy * ex;
    if (Math.abs(den) < 1e-12) continue;
    const u = ((ax - px) * dy - (ay - py) * dx) / den; // position along the segment
    if (u < -1e-9 || u > 1 + 1e-9) continue;
    const t = ((ax - px) * ey - (ay - py) * ex) / den;  // position along the support line
    if (!best || Math.abs(t) < Math.abs(best.t)) best = { t, x: ax + u * ex, y: ay + u * ey };
  }
  if (!best) return null;
  // Newton-style refinement: intersect the support line with the exact tangent at the projected foot
  let x = best.x, y = best.y;
  for (let k = 0; k < 4; k++) {
    const pr = projectToAlignment(x, y, tess);
    const T = getExactTangentAtStation(tess, pr.station);
    if (!T) break;
    const [fx, fy] = pr.foot;
    const den = dx * T.ty - dy * T.tx;
    if (Math.abs(den) < 1e-12) break;
    const t = ((fx - px) * T.ty - (fy - py) * T.tx) / den;
    x = px + t * dx; y = py + t * dy;
  }
  return [x, y];
}

// The bridge's own axis among the file's alignments: { alignment, tess, startChainage, spread } or null
export function findBridgeAxis(supportLines, alignments) {
  const lines = (supportLines || []).filter((l) => l.p0 && l.d && l.station != null);
  if (lines.length < 2) return null;
  let best = null;
  for (const alignment of alignments || []) {
    if (!alignment.path?.length) continue;
    const tess = tessellateAlignment(alignment.path, 0);
    const starts = [];
    for (const l of lines) {
      const c = crossing(l, tess);
      if (!c) break;
      starts.push(l.station - projectToAlignment(c[0], c[1], tess).station);
    }
    if (starts.length !== lines.length) continue;
    const spread = Math.max(...starts) - Math.min(...starts);
    if (spread > AXIS_AGREEMENT) continue;
    const startChainage = starts.reduce((a, b) => a + b, 0) / starts.length;
    if (!best || spread < best.spread) best = { alignment, tess, startChainage, spread };
  }
  return best;
}

// Station / signed offset against an arc's full circle — valid beyond the arc's own sweep too
function arcStationOffset(x, y, prim) {
  const { cx, cy, vX, vY, r, sweepStart, sweepEnd, startStation, endStation } = prim;
  const ux = vX[0] / r, uy = vX[1] / r, wx = vY[0] / r, wy = vY[1] / r;
  const dx = x - cx, dy = y - cy;
  let alpha = (Math.atan2(dx * wx + dy * wy, dx * ux + dy * uy) * 180) / Math.PI - sweepStart;
  while (alpha > 180) alpha -= 360;
  while (alpha <= -180) alpha += 360;
  const sweep = sweepEnd - sweepStart;
  // +1 when the arc turns counter-clockwise in the world (centre on the left), whatever the local frame
  const turn = Math.sign(sweep) * Math.sign(ux * wy - uy * wx);
  return {
    station: startStation + (alpha / sweep) * (endStation - startStation),
    offset: turn * (Math.hypot(dx, dy) - r),
  };
}

// Station / signed offset of a plan point (ORD convention: left of the axis negative). A point beyond
// either end of the axis (piles behind an abutment lie before a clone's start) is measured on the end
// primitive extended, as ORD does — a line along its tangent, an arc around its own circle (checked
// against ORD on Bridge_02, whose axis is an arc) — instead of being clamped to the end point. A spiral
// end falls back to its tangent; no bridge axis in this data ends on one.
export function stationOffset(x, y, tess, startChainage) {
  const { points, cumulLengths, primitives } = tess;
  const length = cumulLengths[cumulLengths.length - 1];
  for (const [end, at, prim] of [[points[0], 0, primitives[0]], [points[points.length - 1], length, primitives[primitives.length - 1]]]) {
    const T = getExactTangentAtStation(tess, at);
    if (!T) continue;
    const along = (x - end[0]) * T.tx + (y - end[1]) * T.ty;
    if ((at === 0 && along < 0) || (at === length && along > 0)) {
      if (prim?.type === "arc") {
        const r = arcStationOffset(x, y, prim);
        return { station: startChainage + r.station, offset: r.offset };
      }
      const cross = T.tx * (y - end[1]) - T.ty * (x - end[0]); // > 0: left of the axis
      return { station: startChainage + at + along, offset: -cross };
    }
  }
  const pr = projectToAlignment(x, y, tess);
  const T = getExactTangentAtStation(tess, pr.station);
  const left = T ? T.tx * (y - pr.foot[1]) - T.ty * (x - pr.foot[0]) > 0 : false;
  return { station: startChainage + pr.station, offset: left ? -pr.distance : pr.distance };
}
